import { useMemo } from "react";
import type { ComponentType } from "react";
import { useIsProfileFieldsEnabled } from "@circle-react-shared/ProfileFields/useIsProfileFieldsEnabled";
import { useProfileFieldsList } from "./ProfileFieldFilter";
import type { RenderFilter, RenderProfileFieldFilter } from "./types";
import { useCrmTrueLocationFieldAndSearchEnabled } from "./useCrmLocationEnabled";

export const useProfileFieldFilters = ({
  platformFilters = [],
  component,
}: {
  platformFilters: RenderFilter[];
  /** Have a specific prop type */
  component: ComponentType<any>;
}) => {
  const isProfileFieldsEnabled = useIsProfileFieldsEnabled();
  const hasProfileFieldsFilters = Boolean(isProfileFieldsEnabled);

  const isCrmTrueLocationFieldAndSearchEnabled =
    useCrmTrueLocationFieldAndSearchEnabled();

  const {
    getOptions: {
      data: customProfileFields,
      isLoading: isLoadingProfileFields,
    },
  } = useProfileFieldsList();

  const filterProfileFields = (filters: RenderFilter[]) => {
    if (!hasProfileFieldsFilters) {
      // we are not in bussiness plan but we do have the location field
      // so since we will have 2 location fields, we need to remove the old one if geo location is enabled
      if (isCrmTrueLocationFieldAndSearchEnabled) {
        return filters.filter(({ name }) => name !== "location");
      }
      return filters.filter(({ name }) => name !== "geo_location");
    }

    return filters.filter(
      ({ name }) => !["bio", "location", "geo_location"].includes(name),
    );
  };

  const filteredDefaultFilters = filterProfileFields(platformFilters);

  const profileFieldRenderFilters: RenderProfileFieldFilter[] = useMemo(
    () =>
      customProfileFields?.map(customProfileField => ({
        ...customProfileField,
        component,
        name: "profile_field",
      })) ?? [],
    [customProfileFields, component],
  );

  return {
    filterProfileFields,
    filteredDefaultFilters,
    isLoadingProfileFields,
    customProfileFields: profileFieldRenderFilters,
    hasProfileFieldsFilters,
  };
};
